import React, { useEffect, useState } from "react";
import { CircularProgress, IconButton, styled, TextField } from "@mui/material";
import { ClinicInnerButton } from "../clinic/clinicActionButton";
import { ClinicColors } from "../styles/clinicTheme";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import EditIcon from "@mui/icons-material/Edit";

//region [[ Styles ]]

const PatientNotesForm = styled("div")({
  display: "flex",
  alignItems: "end",
  gap: 12,
  "& button": { minWidth: 40, minHeight: 30 },
  "& .MuiOutlinedInput-multiline": {
    padding: 10,
    fontSize: 14,
    borderRadius: 3,
    minHeight: 30,
  },
});
const PatientNotesText = styled("div")({
  background: ClinicColors.textBoxBg,
  padding: 10,
  borderRadius: 3,
  fontSize: 14,
  flex: 1,
  minHeight: 35,
  whiteSpace: "pre-wrap",
});
//endregion [[ Styles ]]
//region [[ Props ]]

export interface PatientNotesProps {
  clinicName: string;
  comment: string;
  digitCode: string;
  caseId: string;
  onSaveComment: (
    codeIdToAddCommentTo: string,
    comment: string,
  ) => Promise<any>;
  translate: any;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const PatientNotes = ({
  clinicName,
  caseId,
  ...props
}: PatientNotesProps) => {
  const [orgComment, setOrgComment] = useState<string>();
  const [comment, setComment] = useState<string>();
  const [editMode, setEditMode] = useState<boolean>();
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    setOrgComment(props.comment);
    setComment(props.comment);
    setEditMode(!props.comment);
  }, [props.comment]);

  const saveComment = async () => {
    if (comment === orgComment) {
      setEditMode(false);
      setEditMode(!comment);
      return;
    }

    setSaving(true);
    await props
      .onSaveComment(props.digitCode, comment)
      .then((status) => {
        if (status === "success") {
          evt({
            category: CATEGORY.CLINIC_MANAGEMENT,
            action: ACTION.CLINIC_ADD_DOCTOR_NOTE_ON_PATIENT,
            props: {
              clinicName,
              patientId: caseId,
            },
          });
          setOrgComment(comment);
          setEditMode(!comment);
        }
      })
      .finally(() => setSaving(false));
  };

  return (
    <>
      {editMode && (
        <PatientNotesForm>
          <TextField
            id="standard-multiline-flexible"
            multiline
            minRows={1}
            value={comment}
            variant="outlined"
            placeholder={props.translate("ADD_NOTE")}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
            InputProps={{ style: { padding: "8px 10px" } }}
          />
          <ClinicInnerButton
            onClick={saveComment}
            label={
              orgComment ? props.translate("SAVE") : props.translate("ADD")
            }
            icon={saving && <CircularProgress size={18} />}
            disabled={saving}
          />
        </PatientNotesForm>
      )}
      {!editMode && (
        <PatientNotesForm>
          <PatientNotesText>{comment}</PatientNotesText>
          <IconButton onClick={() => setEditMode(true)} size={"small"}>
            <EditIcon />
          </IconButton>
        </PatientNotesForm>
      )}
    </>
  );
};
